import React, { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { celebrateBadges } from "../lib/gamification";
import { ClipboardList, Loader2 } from "lucide-react";

export default function ConstructedResponse() {
  const [question, setQuestion] = useState("");
  const [draft, setDraft] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const onCoach = async () => {
    if (!question.trim()) { toast.error("Enter a question first."); return; }
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/coach/constructed-response", { question, student_draft: draft });
      setResult(data);
      celebrateBadges(data._meta);
    } catch (e) { toast.error("Could not coach"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-2xl bg-rose-100 text-rose-700 grid place-items-center"><ClipboardList size={20}/></div>
          <h1 className="text-3xl font-bold" style={{fontFamily:'Outfit'}}>Constructed Response Coach</h1>
        </div>
        <p className="text-slate-600">Use the RACE strategy: <strong>R</strong>estate, <strong>A</strong>nswer, <strong>C</strong>ite, <strong>E</strong>xplain.</p>
      </div>

      <div className="tactile-card p-6 sm:p-8 space-y-4">
        <div>
          <label className="text-xs uppercase font-bold tracking-widest text-slate-500">Question</label>
          <textarea data-testid="cr-question" value={question} onChange={(e)=>setQuestion(e.target.value)} rows={3}
            placeholder='e.g. "How does the author show that Maria is brave in chapter 5?"'
            className="mt-1 w-full rounded-2xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-rose-400 px-4 py-3 outline-none resize-none" />
        </div>
        <div>
          <label className="text-xs uppercase font-bold tracking-widest text-slate-500">Your draft (optional)</label>
          <textarea data-testid="cr-draft" value={draft} onChange={(e)=>setDraft(e.target.value)} rows={4}
            placeholder="Take a first attempt — even one sentence helps."
            className="mt-1 w-full rounded-2xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-rose-400 px-4 py-3 outline-none resize-none" />
        </div>
        <button data-testid="cr-submit" onClick={onCoach} disabled={loading}
          className="bg-rose-500 hover:bg-rose-400 text-white font-bold rounded-full px-6 py-3 inline-flex items-center gap-2 disabled:opacity-60"
          style={{boxShadow:'0 4px 0 0 rgb(190 18 60)'}}>
          {loading ? <><Loader2 className="animate-spin" size={16}/> Coaching...</> : "Coach me"}
        </button>
      </div>

      {result && (
        <div data-testid="cr-result" className="space-y-4 fade-up">
          {(result.steps || []).map((s, i) => (
            <div key={i} className="tactile-card p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-xl bg-indigo-600 text-white grid place-items-center font-bold">{i+1}</div>
                <h3 className="text-xl font-bold" style={{fontFamily:'Outfit'}}>{s.name}</h3>
              </div>
              <p className="text-slate-700 mb-3">{s.guidance}</p>
              {s.sentence_starters?.length > 0 && (
                <div className="bg-amber-50 rounded-2xl p-4 border-2 border-amber-100">
                  <div className="text-xs uppercase font-bold tracking-widest text-amber-800 mb-2">Sentence starters</div>
                  <ul className="space-y-1 text-slate-800">
                    {s.sentence_starters.map((ss, idx) => <li key={idx}>"{ss}"</li>)}
                  </ul>
                </div>
              )}
            </div>
          ))}
          {result.draft_feedback && (
            <div className="tactile-card p-6 bg-emerald-50 border-emerald-100">
              <div className="text-xs uppercase font-bold tracking-widest text-emerald-800 mb-2">Coach feedback on your draft</div>
              <p className="text-slate-800">{result.draft_feedback}</p>
            </div>
          )}
          {result.next_action && (
            <div className="tactile-card p-6 bg-indigo-50 border-indigo-100">
              <div className="text-xs uppercase font-bold tracking-widest text-indigo-800 mb-2">Next step</div>
              <p className="text-slate-900 font-semibold">{result.next_action}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
